/**
 * AddOrder Page
 *
 * Allows staff to search for existing Shopify orders and register them
 * as tracked orders in the production pipeline.
 */

import {
  Page,
  Layout,
  Card,
  TextField,
  Button,
  IndexTable,
  Text,
  Badge,
  InlineStack,
  Banner,
  SkeletonBodyText,
  EmptyState,
  Spinner,
  Modal,
  BlockStack,
  Box,
  Tabs,
} from '@shopify/polaris';
import { SearchIcon, PlusCircleIcon } from '@shopify/polaris-icons';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  searchShopifyOrders,
  registerOrder,
  searchShopifyDraftOrders,
  registerDraftOrder,
  ShopifyOrderSearchResult,
  ShopifyDraftOrderSearchResult,
  ApiClientError,
} from '../api';

// Helper to format date
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Helper to format currency
function formatCurrency(amount: string, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(parseFloat(amount));
}

// Helper to get customer name from order or draft order
function getCustomerName(item: ShopifyOrderSearchResult | ShopifyDraftOrderSearchResult): string {
  if (!item.customer) return 'Guest';
  const { firstName, lastName } = item.customer;
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  return name || item.customer.email || 'Guest';
}

// Loading skeleton
function SearchSkeleton() {
  return (
    <Card padding="0">
      <div style={{ padding: '16px' }}>
        <SkeletonBodyText lines={5} />
      </div>
    </Card>
  );
}

export default function AddOrder() {
  const navigate = useNavigate();

  // Tab state
  const [selectedTab, setSelectedTab] = useState(0);
  const tabs = [
    { id: 'orders', content: 'Orders' },
    { id: 'drafts', content: 'Draft Orders' },
  ];
  const isDraftTab = selectedTab === 1;

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [orders, setOrders] = useState<ShopifyOrderSearchResult[]>([]);
  const [draftOrders, setDraftOrders] = useState<ShopifyDraftOrderSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState<string | undefined>();

  // Registration state
  const [selectedOrder, setSelectedOrder] = useState<ShopifyOrderSearchResult | null>(null);
  const [selectedDraftOrder, setSelectedDraftOrder] = useState<ShopifyDraftOrderSearchResult | null>(null);
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [notes, setNotes] = useState('');

  // Handle tab change — clear results
  const handleTabChange = useCallback((index: number) => {
    setSelectedTab(index);
    setOrders([]);
    setDraftOrders([]);
    setHasSearched(false);
    setError(null);
    setHasNextPage(false);
    setEndCursor(undefined);
  }, []);

  // Search for orders or draft orders
const handleSearch = useCallback(async () => {
  const trimmedQuery = searchQuery.trim();
  if (!trimmedQuery) return;

  setLoading(true);
  setError(null);
  setHasSearched(true);

  try {
    let result;
    if (isDraftTab) {
      result = await searchShopifyDraftOrders(trimmedQuery);
      setDraftOrders(result.draftOrders);
    } else {
      result = await searchShopifyOrders(trimmedQuery);
      setOrders(result.orders);
    }
    setHasNextPage(result.hasNextPage);
    setEndCursor(result.endCursor);
  } catch (err) {
    const message = err instanceof ApiClientError ? err.message : `Failed to search ${isDraftTab ? 'draft orders' : 'orders'}`;
    setError(message);
    setOrders([]);
    setDraftOrders([]);
  } finally {
    setLoading(false);
  }
}, [searchQuery, isDraftTab]);

  // Load more results
  const handleLoadMore = useCallback(async () => {
    if (!endCursor) return;

    setLoading(true);
    setError(null);

    try {
      if (isDraftTab) {
        const result = await searchShopifyDraftOrders(searchQuery.trim(), 10, endCursor);
        setDraftOrders((prev) => [...prev, ...result.draftOrders]);
        setHasNextPage(result.hasNextPage);
        setEndCursor(result.endCursor);
      } else {
        const result = await searchShopifyOrders(searchQuery.trim(), 10, endCursor);
        setOrders((prev) => [...prev, ...result.orders]);
        setHasNextPage(result.hasNextPage);
        setEndCursor(result.endCursor);
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to load more results';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, endCursor, isDraftTab]);

  // Open registration confirmation modal for orders
  const handleRegisterClick = useCallback((order: ShopifyOrderSearchResult) => {
    setSelectedOrder(order);
    setSelectedDraftOrder(null);
    setNotes('');
    setRegisterError(null);
    setShowConfirmModal(true);
  }, []);

  // Open registration confirmation modal for draft orders
  const handleRegisterDraftClick = useCallback((draft: ShopifyDraftOrderSearchResult) => {
    setSelectedDraftOrder(draft);
    setSelectedOrder(null);
    setNotes('');
    setRegisterError(null);
    setShowConfirmModal(true);
  }, []);

  // Close modal
  const handleCloseModal = useCallback(() => {
    setShowConfirmModal(false);
    setSelectedOrder(null);
    setSelectedDraftOrder(null);
    setNotes('');
    setRegisterError(null);
  }, []);

  // Confirm registration
  const handleConfirmRegister = useCallback(async () => {
    if (!selectedOrder && !selectedDraftOrder) return;

    setRegistering(true);
    setRegisterError(null);

    try {
      if (selectedDraftOrder) {
        const result = await registerDraftOrder(selectedDraftOrder.id, notes || undefined);
        if (result.success) {
          navigate(`/orders/${result.order.id}`);
        }
      } else if (selectedOrder) {
        const result = await registerOrder(selectedOrder.id, notes || undefined);
        if (result.success) {
          navigate(`/orders/${result.order.id}`);
        }
      }
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : `Failed to register ${selectedDraftOrder ? 'draft order' : 'order'}`;
      setRegisterError(message);
    } finally {
      setRegistering(false);
    }
  }, [selectedOrder, selectedDraftOrder, notes, navigate]);

  // Row markup for orders
  const orderRowMarkup = orders.map((order, index) => {
    const lineItemCount = order.lineItems.edges.length;
    const itemText = lineItemCount === 1 ? '1 item' : `${lineItemCount} items`;
    const total = formatCurrency(
      order.totalPriceSet.shopMoney.amount,
      order.totalPriceSet.shopMoney.currencyCode
    );

    return (
      <IndexTable.Row id={order.id} key={order.id} position={index}>
        <IndexTable.Cell>
          <Text as="span" variant="bodyMd" fontWeight="bold">
            {order.name}
          </Text>
        </IndexTable.Cell>
        <IndexTable.Cell>{getCustomerName(order)}</IndexTable.Cell>
        <IndexTable.Cell>{itemText}</IndexTable.Cell>
        <IndexTable.Cell>{total}</IndexTable.Cell>
        <IndexTable.Cell>{formatDate(order.createdAt)}</IndexTable.Cell>
        <IndexTable.Cell>
          {order.isTracked ? (
            <Badge tone="success">Already Tracked</Badge>
          ) : (
            <Button
              size="slim"
              icon={PlusCircleIcon}
              onClick={() => handleRegisterClick(order)}
            >
              Add
            </Button>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  // Row markup for draft orders
  const draftRowMarkup = draftOrders.map((draft, index) => {
    const lineItemCount = draft.lineItems.edges.length;
    const itemText = lineItemCount === 1 ? '1 item' : `${lineItemCount} items`;
    const total = formatCurrency(
      draft.totalPriceSet.shopMoney.amount,
      draft.totalPriceSet.shopMoney.currencyCode
    );

    return (
      <IndexTable.Row id={draft.id} key={draft.id} position={index}>
        <IndexTable.Cell>
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="bodyMd" fontWeight="bold">
              {draft.name}
            </Text>
            <Badge tone="warning">Draft</Badge>
          </InlineStack>
        </IndexTable.Cell>
        <IndexTable.Cell>{getCustomerName(draft)}</IndexTable.Cell>
        <IndexTable.Cell>{itemText}</IndexTable.Cell>
        <IndexTable.Cell>{total}</IndexTable.Cell>
        <IndexTable.Cell>{formatDate(draft.createdAt)}</IndexTable.Cell>
        <IndexTable.Cell>
          {draft.isTracked ? (
            <Badge tone="success">Already Tracked</Badge>
          ) : draft.order ? (
            <Badge tone="info">Completed</Badge>
          ) : (
            <Button
              size="slim"
              icon={PlusCircleIcon}
              onClick={() => handleRegisterDraftClick(draft)}
            >
              Add
            </Button>
          )}
        </IndexTable.Cell>
      </IndexTable.Row>
    );
  });

  const currentItems = isDraftTab ? draftOrders : orders;
  const rowMarkup = isDraftTab ? draftRowMarkup : orderRowMarkup;

  return (
    <Page
      title="Add Existing Order"
      backAction={{ content: 'Orders', onAction: () => navigate('/orders') }}
      subtitle="Search for a Shopify order or draft order to add to the production pipeline"
    >
      {error && (
        <Layout.Section>
          <Banner
            title="Error"
            tone="critical"
            onDismiss={() => setError(null)}
          >
            <p>{error}</p>
          </Banner>
        </Layout.Section>
      )}

      <Layout>
        {/* Tabs */}
        <Layout.Section>
          <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} />
        </Layout.Section>

        {/* Search Input */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="center">
              <div
                style={{ flex: 1 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              >
                <TextField
                  label={isDraftTab ? 'Search Shopify draft orders' : 'Search Shopify orders'}
                  labelHidden
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={isDraftTab
                    ? 'Enter draft order number, customer name, or email...'
                    : 'Enter order number, customer name, or email...'}
                  autoComplete="off"
                  clearButton
                  onClearButtonClick={() => {
                    setSearchQuery('');
                    setOrders([]);
                    setDraftOrders([]);
                    setHasSearched(false);
                  }}
                />
              </div>
              <Button
                variant="primary"
                icon={SearchIcon}
                onClick={handleSearch}
                loading={loading && !currentItems.length}
              >
                Search
              </Button>
            </InlineStack>
            <Box paddingBlockStart="200">
              <Text as="p" variant="bodySm" tone="subdued">
                {isDraftTab
                  ? 'Search by draft order number (e.g., D1), customer name, or email address'
                  : 'Search by order number (e.g., #1234), customer name, or email address'}
              </Text>
            </Box>
          </Card>
        </Layout.Section>

        {/* Results */}
        <Layout.Section>
          {loading && currentItems.length === 0 ? (
            <SearchSkeleton />
          ) : currentItems.length === 0 && hasSearched ? (
            <Card>
              <EmptyState
                heading={isDraftTab ? 'No draft orders found' : 'No orders found'}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>Try a different search term</p>
              </EmptyState>
            </Card>
          ) : currentItems.length > 0 ? (
            <Card padding="0">
              <IndexTable
                resourceName={isDraftTab
                  ? { singular: 'draft order', plural: 'draft orders' }
                  : { singular: 'order', plural: 'orders' }}
                itemCount={currentItems.length}
                selectable={false}
                headings={[
                  { title: 'Order' },
                  { title: 'Customer' },
                  { title: 'Products' },
                  { title: 'Total' },
                  { title: 'Date' },
                  { title: 'Action' },
                ]}
              >
                {rowMarkup}
              </IndexTable>

              {/* Load More */}
              {hasNextPage && (
                <div style={{ padding: '16px', borderTop: '1px solid #e1e3e5', textAlign: 'center' }}>
                  <Button onClick={handleLoadMore} loading={loading}>
                    Load More
                  </Button>
                </div>
              )}

              {loading && currentItems.length > 0 && (
                <div style={{ padding: '16px', textAlign: 'center' }}>
                  <Spinner size="small" />
                </div>
              )}
            </Card>
          ) : null}
        </Layout.Section>
      </Layout>

      {/* Confirmation Modal */}
      <Modal
        open={showConfirmModal}
        onClose={handleCloseModal}
        title={`Add ${selectedOrder?.name || selectedDraftOrder?.name} to Pipeline`}
        primaryAction={{
          content: 'Add to Pipeline',
          onAction: handleConfirmRegister,
          loading: registering,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleCloseModal,
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {registerError && (
              <Banner tone="critical">
                <p>{registerError}</p>
              </Banner>
            )}

            {selectedDraftOrder && (
              <>
                <Banner tone="warning">
                  <p>
                    This is a <strong>draft order</strong> — payment has not been collected yet.
                    The order will be tracked as a draft until it is completed in Shopify.
                  </p>
                </Banner>

                <Text as="p">
                  This will add draft order <strong>{selectedDraftOrder.name}</strong> to the
                  production pipeline. The order will start in the &quot;Order Received&quot; stage.
                </Text>

                <Text as="h3" variant="headingMd">
                  Draft Order Details
                </Text>

                <BlockStack gap="200">
                  <InlineStack gap="200">
                    <Text as="span" fontWeight="semibold">Customer:</Text>
                    <Text as="span">{getCustomerName(selectedDraftOrder)}</Text>
                  </InlineStack>
                  <InlineStack gap="200">
                    <Text as="span" fontWeight="semibold">Date:</Text>
                    <Text as="span">{formatDate(selectedDraftOrder.createdAt)}</Text>
                  </InlineStack>
                  <InlineStack gap="200">
                    <Text as="span" fontWeight="semibold">Products:</Text>
                    <Text as="span">
                      {selectedDraftOrder.lineItems.edges.length} item(s)
                    </Text>
                  </InlineStack>
                </BlockStack>

                <TextField
                  label="Notes (optional)"
                  value={notes}
                  onChange={setNotes}
                  multiline={2}
                  placeholder="Add any notes about why this draft order is being added..."
                  autoComplete="off"
                />
              </>
            )}

            {selectedOrder && (
              <>
                <Text as="p">
                  This will add order <strong>{selectedOrder.name}</strong> to the production
                  pipeline. The order will start in the &quot;Order Received&quot; stage.
                </Text>

                <Text as="h3" variant="headingMd">
                  Order Details
                </Text>

                <BlockStack gap="200">
                  <InlineStack gap="200">
                    <Text as="span" fontWeight="semibold">Customer:</Text>
                    <Text as="span">{getCustomerName(selectedOrder)}</Text>
                  </InlineStack>
                  <InlineStack gap="200">
                    <Text as="span" fontWeight="semibold">Date:</Text>
                    <Text as="span">{formatDate(selectedOrder.createdAt)}</Text>
                  </InlineStack>
                  <InlineStack gap="200">
                    <Text as="span" fontWeight="semibold">Products:</Text>
                    <Text as="span">
                      {selectedOrder.lineItems.edges.length} item(s)
                    </Text>
                  </InlineStack>
                </BlockStack>

                <TextField
                  label="Notes (optional)"
                  value={notes}
                  onChange={setNotes}
                  multiline={2}
                  placeholder="Add any notes about why this order is being added manually..."
                  autoComplete="off"
                />
              </>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
